import numeral from 'numeral';
import querystring from 'querystring';
import client from '@/api';

export default {

    buildCost(cost) {
        const data = {
            group_id: cost.groupId || cost.group_id,
            commission: numeral(cost.commission.value).value(),
            commission_type: cost.commission.type,
            is_fixed_commission: cost.commission.fixed,
            description: cost.description,
            management_fee: numeral(cost.management_fee).value(),
            discount: numeral(cost.discount.value).value(),
            is_fixed_discount: cost.discount.fixed,
            notes: cost.notes,
            use_guest_qty: cost.is_quantity_per_head,
            cost_quantity: numeral(cost.cost_quantity).value(),
            has_cost_quantity: cost.has_cost_quantity,
            original_quantity: numeral(cost.quantity.value).value(),
            quantity_unit: cost.quantity.unit,
            resources: cost.resources.map(resource => ({
                resource_id: resource.resource.id,
                quantity: numeral(resource.quantity).value(),
                notes: resource.notes,
            })),
            unit_cost_currency_id: cost.unit_cost_local.currency,
            unit_cost: cost.unit_cost_local.value,
            unit_price_currency_id: cost.unit_price_local.currency,
            unit_price: cost.unit_price_local.value,
            price_unit: cost.unit_price.unit,
            is_billable: cost.billable,
            is_vatable: cost.vatable,
            display_duration: cost.duration.enabled,
            has_time_based_duration: cost.duration.time.enabled,
            start_time: cost.duration.time.start,
            end_time: cost.duration.time.end,
            duration: numeral(cost.duration.quantity.value).value(),
            duration_unit: cost.duration.quantity.unit,
        };
        if (cost.type) data.cost_type_id = cost.type.id;
        if (cost.status) data.cost_status_id = cost.status.id;
        if (cost.taxRate) data.tax_rate_id = cost.taxRate.id;
        if (cost.supplier) data.supplier_account_id = cost.supplier.id;
        return data;
    },

    getCostable(costableType, costableId, queryStringObject) {
        return client.execute('get', `costables/${costableType}/${costableId}?${querystring.stringify(queryStringObject)}`);
    },

    getProgress(costableType, costableId) {
        return client.execute('get', `costables/${costableType}/${costableId}/progress`);
    },

    /**
     * Store a new cost and add the current tab to the cost object.
     * @param {*} costableType
     * @param {*} costableId
     * @param {*} tabId
     * @param {*} buildCost
     * @param {*} cost
     */
    storeCost(costableType, costableId, tabId, buildCost, cost) {
        const data = buildCost ? this.buildCost(cost) : cost;
        data.tab_id = tabId;
        return client.execute('post', `costables/${costableType}/${costableId}/costs`, data);
    },

    /**
     * We don't need to pass `tabId` here since it's already a part of the cost object.
     * @param {*} costableType
     * @param {*} costableId
     * @param {*} cost
     */
    updateCost(costableType, costableId, cost) {
        const data = this.buildCost(cost);
        return client.execute('put', `costables/${costableType}/${costableId}/costs/${cost.id}`, data);
    },

    patchCosts(costableType, costableId, costIds, data) {
        return client.execute('patch', `costables/${costableType}/${costableId}/costs`, {
            cost_ids: costIds,
            ...data,
        });
    },

    patchCostsMultiplier(costableType, costableId, costIds, data) {
        return client.execute('patch', `costables/${costableType}/${costableId}/costs/multiplier`, {
            cost_ids: costIds,
            ...data,
        });
    },

    duplicateCost(costableType, costableId, costId) {
        return client.execute('post', `costables/${costableType}/${costableId}/costs/${costId}/duplicate`);
    },

    deleteCosts(costableType, costableId, costIds) {
        return client.execute('delete', `costables/${costableType}/${costableId}/costs`, {
            cost_ids: costIds,
        });
    },

    removeCostFromGroup(costableType, costableId, costIds) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/remove-from-group`, {
            cost_ids: costIds,
        });
    },

    removeCostFromTab(costableType, costableId, costIds) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/remove-from-tab`, {
            cost_ids: costIds,
        });
    },

    approveCosts(costableType, costableId, costIds) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/approve`, {
            cost_ids: costIds,
        });
    },

    unapproveCosts(costableType, costableId, costIds) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/unapprove`, {
            cost_ids: costIds,
        });
    },

    reconcileCosts(costableType, costableId, costIds) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/reconcile`, {
            cost_ids: costIds,
        });
    },

    unreconcileCosts(costableType, costableId, costIds) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/unreconcile`, {
            cost_ids: costIds,
        });
    },

    getCostResourceAllocations(costableType, costableId, costId, typeId, supplierId, parentResource, searchQuery,
        onlyAllocated, showArchived, page, useDefaultTypeId, sort, direction) {
        let queryParams = '';
        if (typeId) queryParams = `${queryParams}type_id=${typeId}&`;
        if (supplierId) queryParams = `${queryParams}supplier_id=${supplierId}&`;
        if (parentResource) queryParams = `${queryParams}parent_resource=${parentResource}&`;
        if (searchQuery) queryParams = `${queryParams}search_query=${searchQuery}&`;
        if (onlyAllocated) queryParams = `${queryParams}only_allocated&`;
        if (showArchived) queryParams = `${queryParams}show_archived=${showArchived}&`;
        if (useDefaultTypeId) queryParams = `${queryParams}use_default_type_id&`;
        if (sort) queryParams = `${queryParams}sort=${sort}&`;
        if (direction) queryParams = `${queryParams}direction=${direction}&`;
        return client.execute(
            'get',
            `costables/${costableType}/${costableId}/costs/${costId}/resources?${queryParams}page=${page}`
        );
    },

    patchCostResourceAllocation(costableType, costableId, costId, resourceId, data) {
        return client.execute('put', `costables/${costableType}/${costableId}/costs/${costId}/resources/${resourceId}`, data);
    },

    // search for accounts
    accountSearch(query, onlyWithResources = false) {
        const onlyWithResourcesQueryString = onlyWithResources ? 'only_with_resources=true' : '';
        return client.execute('get', `accounts/search?${onlyWithResourcesQueryString}query=${query}`);
    },

    // search for resources
    resourceSearch(query) {
        return client.execute('get', `resources/search?query=${query}`);
    },

    storeGroup(costableType, costableId, tabId, name, description) {
        return client.execute('post', `costables/${costableType}/${costableId}/groups`, {
            name,
            description,
            tab_id: tabId,
        });
    },

    updateGroup(costableType, costableId, groupId, data) {
        return client.execute('put', `costables/${costableType}/${costableId}/groups/${groupId}`, data);
    },

    duplicateGroup(costableType, costableId, groupId) {
        return client.execute('post', `costables/${costableType}/${costableId}/groups/${groupId}/duplicate`);
    },

    addGroupToTab(costableType, costableId, groupId, tabId) {
        return client.execute('post', `costables/${costableType}/${costableId}/groups/${groupId}/add-to-tab`, {
            tab_id: tabId,
        });
    },

    deleteGroup(costableType, costableId, groupId) {
        return client.execute('delete', `costables/${costableType}/${costableId}/groups/${groupId}`);
    },

    addCostsToGroup(costableType, costableId, costIds, groupId) {
        return client.execute('post', `costables/${costableType}/${costableId}/costs/add-to-group`, {
            cost_ids: costIds,
            group_id: groupId,
        });
    },

    storeTab(costableType, costableId, name, description, footer, hiddenColumns, hideCosts) {
        return client.execute('post', `costables/${costableType}/${costableId}/tabs`, {
            name,
            description,
            footer,
            hidden_columns: hiddenColumns,
            hide_costs: hideCosts,
        });
    },

    updateTab(costableType, costableId, tabId, name, description, footer, hiddenColumns, hideCosts) {
        return client.execute('put', `costables/${costableType}/${costableId}/tabs/${tabId}`, {
            name,
            description,
            footer,
            hidden_columns: hiddenColumns,
            hide_costs: hideCosts,
        });
    },

    duplicateTab(costableType, costableId, tabId, name, description) {
        return client.execute('post', `costables/${costableType}/${costableId}/tabs/${tabId}/duplicate`, {
            name,
            description,
        });
    },

    deleteTab(costableType, costableId, tabId, deleteGroupsAndCosts) {
        const queryParams = deleteGroupsAndCosts ? 'delete_groups_and_costs' : '';
        return client.execute('delete', `costables/${costableType}/${costableId}/tabs/${tabId}?${queryParams}`);
    },

    getPackages() {
        return client.execute('get', 'packages');
    },

    addPackage(costableType, costableId, tabId, packageId, setCurrency) {
        return client.execute('post', `costables/${costableType}/${costableId}/add-package`, {
            package_id: packageId,
            tab_id: tabId,
            set_display_currency: setCurrency,
        });
    },

    updateContingency(costableType, costableId, internalCostPercentage, internalCostMinimum, externalPricePercentage, externalPriceMinimum) {
        return client.execute('put', `costables/${costableType}/${costableId}/update-contingency`, {
            input_contingency: internalCostPercentage,
            minimum_input_contingency: internalCostMinimum,
            contingency: externalPricePercentage,
            minimum_contingency: externalPriceMinimum,
        });
    },

    refreshReportedInformation(costableType, costableId) {
        return client.execute('post', `costables/${costableType}/${costableId}/refresh-reported-information`);
    },

    getAvailableCurrencies(costableType, costableId) {
        return client.execute('get', `costables/${costableType}/${costableId}/available-currencies`);
    },

    storeCurrency(costableType, costableId, currencyId, exchangeRate) {
        return client.execute('post', `costables/${costableType}/${costableId}/currencies`, {
            currency_id: currencyId,
            exchange_rate: exchangeRate,
        });
    },

    updateCurrency(costableType, costableId, currencyId, exchangeRate) {
        return client.execute('put', `costables/${costableType}/${costableId}/currencies/${currencyId}`, {
            exchange_rate: exchangeRate,
        });
    },

    deleteCurrency(costableType, costableId, currencyId) {
        return client.execute('delete', `costables/${costableType}/${costableId}/currencies/${currencyId}`);
    },

    getEventPricingMode(costableType, costableId) {
        return client.execute('get', `costables/${costableType}/${costableId}/event-pricing-mode`);
    },

    updateEventPricingMode(costableType, costableId, pricingMode, feeAmount, feeTaxRateId) {
        return client.execute('put', `costables/${costableType}/${costableId}/event-pricing-mode`, {
            pricing_mode: pricingMode,
            fee_amount: feeAmount,
            fee_tax_rate_id: feeTaxRateId,
        });
    },

    getQuotePricingMode(costableType, costableId, acceptedQuoteId) {
        return client.execute('get', `costables/${costableType}/${costableId}/quote-pricing-mode?quote_id=${acceptedQuoteId}`);
    },

    updateQuotePricingMode(costableType, costableId, acceptedQuoteId, pricingMode, feeAmount, feeTaxRateId) {
        return client.execute('put', `costables/${costableType}/${costableId}/quote-pricing-mode`, {
            quote_id: acceptedQuoteId,
            pricing_mode: pricingMode,
            fee_amount: feeAmount,
            fee_tax_rate_id: feeTaxRateId,
        });
    },

    updateGroupsOrder(costableType, costableId, groupIds) {
        return client.execute('post', `costables/${costableType}/${costableId}/groups/update-order`, {
            group_ids: groupIds,
        });
    },

    updateGroupCostsOrder(costableType, costableId, costIds, groupId) {
        return client.execute('post', `costables/${costableType}/${costableId}/costs/update-order`, {
            cost_ids: costIds,
            group_id: groupId,
        });
    },

    updateTabsOrder(costableType, costableId, tabIds) {
        return client.execute('post', `costables/${costableType}/${costableId}/tabs/update-order`, {
            tab_ids: tabIds,
        });
    },

    getSettings() {
        return client.execute('get', 'settings');
    },

    patchCostable(costableType, costableId, data) {
        return client.execute('patch', `costables/${costableType}/${costableId}`, data);
    },

    saveCostAsQuote(costableType, costableId, costIds) {
        const app = this;

        return client.execute('put', `costables/${costableType}/${costableId}/costs/save-as-quote`, {
            cost_ids: costIds,
        }).then((response) => {
            app.quote = response;
            const path = `/admin/crm/events/${app.quote.event_id}/quotes/${app.quote.id}`;
            window.location.href = path;
        });
    },

    async saveColumnSettingsttings(columns) {
        await client.execute('post', 'settings', {
            hidden_columns: columns,
        });
    },

};
